import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { observable } from "mobx";

import {
  DefaultPatientThresholdResponse,
  DefaultAlertThreshold,
  PermissionBit,
  SuppressingDiagnosis,
  ThresholdTables,
} from "../../../api";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { Merge } from "../../../utils/types";
import { DiagnosesItem } from "../diagnoses/diagnoses";

export type ThresholdsItem = Merge<
  DefaultPatientThresholdResponse | DefaultAlertThreshold
> & {
  isDefault?: boolean;
  /** Synthesized row identity — default thresholds have no uuid. */
  key?: string;
};

/**
 * A diagnosis that mutes a default threshold, paired with the patient's own
 * value when they actually carry it — `matchedValue` non-null means this
 * default is off for this patient.
 */
export type ThresholdSuppression = {
  diagnosis: SuppressingDiagnosis;
  matchedValue: string | null;
};

export class Thresholds extends DataModel<ThresholdsItem> {
  @observable accessor defaultThresholds: DefaultAlertThreshold[];
  /**
   * The patient's diagnoses, for cross-referencing `suppressing_diagnoses`.
   * Stays empty when the user can't read them (see `thresholds-table.tsx`),
   * which degrades to showing the declared suppressors without marking any
   * row as actually suppressed.
   */
  @observable accessor diagnoses: DiagnosesItem[] = [];
  // constants
  threshTables: Record<ThresholdTables, MessageDescriptor> = {
    [ThresholdTables.medicaldata]: msg`Medical data (Manual)`,
    [ThresholdTables.symptoms]: msg`Symptoms (Manual)`,
    [ThresholdTables.ppg_derived]: msg`PPG-derived (from Oximeter)`,
    [ThresholdTables.sq_computed]: msg`SQ-computed (our algorithm)`,
    [ThresholdTables.patients]: msg`Patient`,
    [ThresholdTables.ppg]: msg`PPG`,
  };
  threshTriggers: Record<string, MessageDescriptor> = {
    value_based: msg`Value-based`,
    change_based: msg`Change-based`,
  };

  constructor(
    data: DefaultPatientThresholdResponse[],
    defaultThresholds: DefaultAlertThreshold[],
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super(data, permissions, userUUID);
    this.defaultThresholds = defaultThresholds;

    // access
    this.access = {
      editRules: { medical: AccessRules.none, monitoring: AccessRules.visible },
      deleteRules: {
        medical: AccessRules.none,
        monitoring: AccessRules.visible,
      },
      historyRequiresMonitoring: false,
    };
  }

  uniqueKey(item: any) {
    return `${item["threshold_variable"]}|${item["threshold_type"]}|${item["threshold_trigger"]}|${JSON.stringify(item["threshold_settings"])}`;
  }

  init() {
    this.defaultThresholds = this.defaultThresholds.map((item) => {
      return {
        ...item,
        ["isDefault"]: true,
        ["threshold_comment"]: "default threshold",
      };
    });
    this.data = this.data.map((item) => {
      return { ...item, ["isDefault"]: false };
    });
    const existingThreshs = new Set(
      this.data.map((item) => this.uniqueKey(item)),
    );
    const defaultsFilt = this.defaultThresholds.filter(
      (item) => !existingThreshs.has(this.uniqueKey(item)),
    );
    this.data = this.data.concat(defaultsFilt);
    // DataTable renders model.data directly (it doesn't call sortFilterData),
    // so apply the custom ordering here and synthesize a stable row key.
    this.data = this.sortThresholds(this.data).map((item) => ({
      ...item,
      key: this.uniqueKey(item),
    }));
    super.init();
  }

  sortThresholds(thresholds: any[]) {
    const tableOrder = [
      "medicaldata",
      "symptoms",
      "sq_computed",
      "ppg_derived",
    ];
    const triggerOrder = ["value_based", "change_based"];
    const typeOrder = ["high", "low"];

    return thresholds.sort((a, b) => {
      // 0. patient-specific first
      if (a.isDefault !== b.isDefault) {
        return a.isDefault - b.isDefault;
      }
      // 1. order by table
      const tableA = tableOrder.indexOf(a.threshold_variable_table);
      const tableB = tableOrder.indexOf(b.threshold_variable_table);
      if (tableA !== tableB) return tableA - tableB;
      // 2. order by name
      if (a.threshold_variable !== b.threshold_variable) {
        return a.threshold_variable.localeCompare(b.threshold_variable);
      }
      // 3. order by trigger
      const orderA = triggerOrder.indexOf(a.threshold_trigger);
      const orderB = triggerOrder.indexOf(b.threshold_trigger);
      if (orderA !== orderB) return orderA - orderB;
      // 4. order by type
      const typeA = typeOrder.indexOf(a.threshold_type);
      const typeB = typeOrder.indexOf(b.threshold_type);
      if (typeA !== typeB) return typeA - typeB;

      // 5. order by value
      if (
        a.threshold_type === typeOrder[0] &&
        b.threshold_type === typeOrder[0]
      ) {
        if (a.threshold_value !== b.threshold_value)
          return b.threshold_value - a.threshold_value;
      }
      if (
        a.threshold_type === typeOrder[1] &&
        b.threshold_type === typeOrder[1]
      ) {
        if (a.threshold_value !== b.threshold_value)
          return a.threshold_value - b.threshold_value;
      }
      // 6. order by settings
      if (
        a.threshold_trigger === triggerOrder[0] &&
        b.threshold_trigger === triggerOrder[0]
      ) {
        return a.threshold_settings.at_least - b.threshold_settings.at_least;
      }
      if (
        a.threshold_trigger === triggerOrder[1] &&
        b.threshold_trigger === triggerOrder[1]
      ) {
        return (
          a.threshold_settings.look_back_days -
          b.threshold_settings.look_back_days
        );
      }
    });
  }

  settingsString(thresh: any) {
    const settings = thresh.threshold_settings;
    if (thresh.threshold_trigger === "value_based") {
      return `${settings.at_least} / ${settings.out_of}`;
    } else {
      let agg = settings.aggregation;
      agg = agg === "min" ? "max" : agg;
      return i18n._(
        msg`${agg} ${settings.change_type} over ${settings.look_back_days} day(s)`,
      );
    }
  }

  /**
   * The suppressing diagnoses declared on a default threshold, each resolved
   * against the patient's own diagnoses. Empty for patient-specific rows and
   * for defaults that declare none.
   */
  suppressions(item: ThresholdsItem): ThresholdSuppression[] {
    if (!item.isDefault || !item.suppressing_diagnoses?.length) {
      return [];
    }
    return item.suppressing_diagnoses.map((diagnosis) => ({
      diagnosis,
      matchedValue: matchSuppressingDiagnosis(diagnosis, this.diagnoses),
    }));
  }

  /** Whether this default is muted for the current patient. */
  isSuppressed(item: ThresholdsItem): boolean {
    return this.suppressions(item).some((s) => s.matchedValue !== null);
  }

  override validateItemEdit(item: any): string | undefined {
    if (item.isDefault) {
      return i18n._(msg`Cannot edit`);
    }

    return super.validateItemEdit(item);
  }

  override validateItemDelete(item: any): string | undefined {
    if (item.isDefault) {
      return i18n._(msg`Cannot delete`);
    }

    return super.validateItemDelete(item);
  }
}

/**
 * The parts of a diagnosis that suppression looks at. Declared structurally
 * rather than as `DiagnosesItem`, whose per-comorbidity variants pin
 * `diagnosis_value` to different string literals.
 */
export type SuppressionCandidate = {
  diagnosis_name: string;
  diagnosis_value: string | number;
  remission?: string | null;
};

/**
 * The patient's value for a suppressing diagnosis, or null when they don't
 * carry it. Mirrors the backend's `is_diagnosis_suppressed`: the diagnosis has
 * to match by name, be active (no remission), and hold one of the suppressing
 * values.
 */
export function matchSuppressingDiagnosis(
  suppressing: SuppressingDiagnosis,
  diagnoses: SuppressionCandidate[],
): string | null {
  const match = diagnoses.find(
    (diagnosis) =>
      diagnosis.diagnosis_name === suppressing.diagnosis_name &&
      diagnosis.remission == null &&
      suppressing.suppressing_values.includes(
        String(diagnosis.diagnosis_value),
      ),
  );
  return match ? String(match.diagnosis_value) : null;
}
